import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { DialogDriverService } from '@app/services/dialog-driver.service';
import { IAccident } from '@app/models/accidents.model';

@Component({
  selector: 'app-step-time-place',
  templateUrl: './step-time-place.component.html',
  styleUrls: ['./step-time-place.component.scss'],
  standalone: false,
})
export class StepTimePlaceComponent implements OnInit, OnDestroy, OnChanges {
  @Input() timePlaceForm!: FormGroup;
  @Input() driverData!: any;
  @Input() driverInfoForm!: FormGroup;
  @Output() closedDialog = new EventEmitter<void>();
  @Output() editExistingAccident = new EventEmitter<IAccident>();

  private destroy$ = new Subject<void>();

  existingAccidents: IAccident[] = [];
  duplicateAccident: IAccident | null = null;
  showDuplicateWarning = false;

  date: string = '';
  time: string = '';

  constructor(private dialogDriverService: DialogDriverService) {}

  ngOnInit(): void {
    // Initialize with current date and time
    const now = new Date();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const year = now.getFullYear();
    this.date = `${month}/${day}/${year}`;

    let hours = now.getHours();
    const minutes = now.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // convert 0 to 12 for 12-hour format
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    this.time = `${hours}:${formattedMinutes} ${ampm}`;

    // Update form values
    this.updateDate(this.date);
    this.updateTime(this.time);

    // Subscribe to date changes to check for duplicate accidents
    this.timePlaceForm
      .get('date')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((date) => {
        const driverId = this.getDriverId();
        if (date && driverId) {
          this.checkForDuplicateAccident(date);
        }
      });

    // Subscribe to driver selection changes to reload accidents
    if (this.driverInfoForm) {
      this.driverInfoForm
        .get('driverId')
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((driverId) => {
          if (driverId) {
            this.loadExistingAccidents();
          } else {
            // Clear accidents when driver is deselected
            this.existingAccidents = [];
            this.showDuplicateWarning = false;
            this.duplicateAccident = null;
          }
        });
    }

    // Load existing accidents if driver data is already available
    this.initializeDriverDataIfAvailable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if driverInfoForm has changed
    if (changes['driverInfoForm'] && changes['driverInfoForm'].currentValue) {
      this.setupDriverFormSubscription();
    }
  }

  private setupDriverFormSubscription(): void {
    if (this.driverInfoForm) {
      this.driverInfoForm
        .get('driverId')
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((driverId) => {
          if (driverId) {
            this.loadExistingAccidents();
          } else {
            // Clear accidents when driver is deselected
            this.existingAccidents = [];
            this.showDuplicateWarning = false;
            this.duplicateAccident = null;
          }
        });
    }
  }

  private getDriverId(): string | null {
    return this.driverInfoForm?.get('driverId')?.value || null;
  }

  private initializeDriverDataIfAvailable(): void {
    const driverId = this.getDriverId();
    if (driverId) {
      this.loadExistingAccidents();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateTime(time: string): void {
    this.timePlaceForm.get('time')?.setValue(time);
  }

  updateDate(date: string): void {
    this.timePlaceForm.get('date')?.setValue(date);
    if (this.driverData?.driverGUID) {
      this.checkForDuplicateAccident(date);
    }
  }

  private loadExistingAccidents(): void {
    const driverId = this.getDriverId();
    if (!driverId) {
      return;
    }

    // Get accidents from the last year
    const endDate = new Date();
    const startDate = new Date();
    startDate.setFullYear(endDate.getFullYear() - 1);

    const request = {
      startDate: startDate,
      endDate: endDate,
      driverGUID: driverId,
    };

    this.dialogDriverService.getAccidentsSummary(request).subscribe({
      next: (accidents: IAccident[]) => {
        this.existingAccidents = accidents;
        // Check for duplicates with current date
        const currentDate = this.timePlaceForm.get('date')?.value;
        if (currentDate) {
          this.checkForDuplicateAccident(currentDate);
        }
      },
      error: (error) => {
        console.error('Error loading existing accidents:', error);
      },
    });
  }

  private checkForDuplicateAccident(selectedDate: string): void {
    if (!selectedDate || !this.existingAccidents.length) {
      this.showDuplicateWarning = false;
      this.duplicateAccident = null;
      return;
    }

    // Convert selected date to match format from API (YYYY-MM-DD)
    const [month, day, year] = selectedDate.split('/');
    const formattedSelectedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(
      2,
      '0'
    )}`;

    // Find accident on the same date
    this.duplicateAccident =
      this.existingAccidents.find((accident) => {
        if (!accident.accidentDate) return false;

        // Extract date part from accident date (YYYY-MM-DDTHH:mm:ss)
        const accidentDatePart = accident.accidentDate.split('T')[0];
        return accidentDatePart === formattedSelectedDate;
      }) || null;

    this.showDuplicateWarning = !!this.duplicateAccident;
  }

  onEditExistingAccident(): void {
    if (!this.duplicateAccident) return;

    // Emit the accident data to the parent component
    this.editExistingAccident.emit(this.duplicateAccident);
  }
}
